import React from 'react';
import { useParams } from 'react-router';

// Module Components
import DirectionBoundary from '../components/DirectionBoundary';
import EnvironmentNotice from '../components/EnvironmentNotice';
import { CardEditor } from '../components/editor';

// Contexts
import { CardEditorProvider } from '../contexts';

// Hooks
import { useChannelRepository } from '../hooks';

// Types
import { ProductCardsRepository } from '../types';

interface CardEditorViewProps {
  /**
   * The persistence port. Left unset in the app, where the HTTP repository for
   * the selected environment is built here (ADR-001 — the adapter landed
   * 2026-08-18); supplied by tests to stay off the network.
   */
  repository?: ProductCardsRepository;
}

/**
 * Route entry for `/gms/product_cards/:cardId` — the Card Editor for one card
 * (spec R17, R28).
 */
const CardEditorView = ({ repository }: CardEditorViewProps) => {
  // react-router v7 types every param as possibly-undefined; this component
  // only renders under CARD_EDITOR_ROUTE_PATTERN, so a missing id means a
  // malformed link — the provider's own not-found state handles ''.
  const { cardId = '' } = useParams();
  const channelRepository = useChannelRepository();
  const activeRepository = repository ?? channelRepository;

  // No environment selected — nothing to load a card from. The same notice the
  // listing shows, so a deep link into the editor fails the same honest way.
  if (!activeRepository) {
    return (
      <DirectionBoundary>
        <EnvironmentNotice />
      </DirectionBoundary>
    );
  }

  return (
    // `tw-contents` keeps this wrapper out of the layout entirely: the editor
    // declares its own height against `main` (see CardEditor's EDITOR_HEIGHT_CLASS)
    // and an extra block-level box between them would break that chain.
    <DirectionBoundary className="tw-contents">
      <CardEditorProvider cardId={cardId} repository={activeRepository}>
        <CardEditor />
      </CardEditorProvider>
    </DirectionBoundary>
  );
};

export default CardEditorView;
